import { useCallback, useState } from 'react';

import { useRenewalSetupStore } from '../stores';
import { RenewalSetupStepKey } from '../stores/useRenewalSetup.store';

import { useValidatePlanCodesOnServer } from './useValidatePlanCodesOnServer';

interface Params {
  step: RenewalSetupStepKey;
  orgId?: string;
  getHasClientErrors: () => boolean;
  getPlanCodes: () => string[];
}

export const useRunActiveCodeValidation = ({ step, orgId, getHasClientErrors, getPlanCodes }: Params) => {
  const setStepState = useRenewalSetupStore((state) => state.setStepState);
  const setIsRevalidationPending = useRenewalSetupStore((state) => state.setIsRevalidationPending);
  const setServerInvalidCodes = useRenewalSetupStore((state) => state.setServerInvalidCodes);
  const { validate: validatePlanCodesOnServer, isValidating } = useValidatePlanCodesOnServer();

  const [showPlansError, setShowPlansError] = useState(
    () => !!useRenewalSetupStore.getState()[step].isFailed
  );

  const runActiveValidation = useCallback(async (): Promise<boolean> => {
    try {
      if (getHasClientErrors()) {
        setShowPlansError(true);
        setStepState(step, { isFailed: true });
        return false;
      }

      const planCodes = getPlanCodes();

      if (planCodes.length > 0) {
        const invalidCodes = await validatePlanCodesOnServer({ orgId, codes: planCodes });

        if (invalidCodes.length > 0) {
          setServerInvalidCodes(step, invalidCodes);
          setShowPlansError(true);
          setStepState(step, { isFailed: true });
          return false;
        }
      }

      setServerInvalidCodes(step, []);
      setShowPlansError(false);
      setStepState(step, { isFailed: false });
      return true;
    } finally {
      setIsRevalidationPending(false);
    }
  }, [
    getHasClientErrors,
    getPlanCodes,
    orgId,
    step,
    setIsRevalidationPending,
    setServerInvalidCodes,
    setStepState,
    validatePlanCodesOnServer,
  ]);

  return {
    runActiveValidation,
    isValidating,
    showPlansError,
    setShowPlansError,
  };
};
